import React, { useState } from 'react';
import { authAPI } from '../../utils/api';
import useSwipeToClose from '../../hooks/useSwipeToClose';
import './AuthModal.css';

const AuthModal = ({ isOpen, onClose, onAuthSuccess }) => {
  const [mode, setMode] = useState('magic-link'); // 'magic-link', 'password', or 'forgot-password'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Add swipe to close functionality
  const swipeRef = useSwipeToClose(onClose, isOpen);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear messages when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  const validateEmailForm = () => {
    if (!formData.email) {
      setError('Email is required');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const validatePasswordForm = () => {
    if (!formData.email || !formData.password) {
      setError('Email and password are required');
      return false;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }

    return true;
  };

  const handleMagicLinkSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateEmailForm()) return;

    try {
      setLoading(true);
      setError('');

      await authAPI.signInWithMagicLink(formData.email);
      setSuccess('Magic link sent! Check your email and click the link to sign in.');
      
    } catch (err) {
      console.error('Magic link error:', err);
      setError(err.message || 'Failed to send magic link');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePasswordForm()) return;

    try {
      setLoading(true);
      setError('');

      await authAPI.signIn(formData.email, formData.password);

      // Reset form
      setFormData({ email: '', password: '' });
      onAuthSuccess();
      onClose();
      
    } catch (err) {
      console.error('Password auth error:', err);
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateEmailForm()) return;

    try {
      setLoading(true);
      setError('');

      await authAPI.requestPasswordReset(formData.email);
      setSuccess('Password reset email sent! Check your inbox for instructions.');
      
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err.message || 'Failed to send password reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleModeSwitch = (newMode) => {
    setMode(newMode);
    setError('');
    setSuccess('');
    setFormData({ email: '', password: '' });
  };

  const handleClose = () => {
    setMode('magic-link');
    setError('');
    setSuccess('');
    setFormData({ email: '', password: '' });
    onClose();
  };

  if (!isOpen) return null;

  const getTitle = () => {
    switch (mode) {
      case 'magic-link': return 'Sign In with Magic Link';
      case 'password': return 'Sign In with Password';
      case 'forgot-password': return 'Reset Password';
      default: return 'Sign In';
    }
  };

  const getSubmitHandler = () => {
    switch (mode) {
      case 'magic-link': return handleMagicLinkSubmit;
      case 'password': return handlePasswordSubmit;
      case 'forgot-password': return handleForgotPasswordSubmit;
      default: return handleMagicLinkSubmit;
    }
  };

  const getSubmitText = () => {
    if (loading) {
      switch (mode) {
        case 'magic-link': return 'Sending Magic Link...';
        case 'password': return 'Signing In...';
        case 'forgot-password': return 'Sending Reset Email...';
        default: return 'Please wait...';
      }
    }
    
    switch (mode) {
      case 'magic-link': return 'Send Magic Link';
      case 'password': return 'Sign In';
      case 'forgot-password': return 'Send Reset Email';
      default: return 'Sign In';
    }
  };

  return (
    <div className="auth-modal-overlay" onClick={handleClose}>
      <div className="auth-modal-content" onClick={e => e.stopPropagation()} ref={swipeRef}>
        <div className="auth-modal-header">
          <h2>{getTitle()}</h2>
          <button className="auth-close-button" onClick={handleClose}>×</button>
        </div>

        <form onSubmit={getSubmitHandler()} className="auth-form">
          {error && <div className="auth-error-message">{error}</div>}
          {success && <div className="auth-success-message">{success}</div>}

          <div className="auth-form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter your email address"
              required
            />
          </div>

          {mode === 'password' && (
            <div className="auth-form-group">
              <label>Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                required
              />
            </div>
          )}

          <button 
            type="submit" 
            disabled={loading}
            className="auth-submit-button"
          >
            {getSubmitText()}
          </button>

          <div className="auth-links">
            {mode === 'magic-link' && (
              <>
                <button
                  type="button"
                  className="auth-link-button"
                  onClick={() => handleModeSwitch('password')}
                >
                  Sign in with password instead
                </button>
              </>
            )}
            
            {mode === 'password' && (
              <>
                <button
                  type="button"
                  className="auth-link-button"
                  onClick={() => handleModeSwitch('magic-link')}
                >
                  Use magic link instead
                </button>
                <button
                  type="button"
                  className="auth-link-button"
                  onClick={() => handleModeSwitch('forgot-password')}
                >
                  Forgot your password?
                </button>
              </>
            )}
            
            {mode === 'forgot-password' && (
              <button
                type="button"
                className="auth-link-button"
                onClick={() => handleModeSwitch('magic-link')}
              >
                Back to Magic Link
              </button>
            )}
          </div>
        </form>

        <div className="auth-toggle">
          <p className="auth-info-text">
            {mode === 'magic-link' 
              ? "We'll send you a secure link to sign in instantly - no password needed!"
              : "Don't have an account? Contact an administrator to request access."
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;